import React, { useState } from "react";
import { Button } from "primereact/button";
import { AppCard, AppBadge } from "../../components/ui";
import { Dropdown } from "primereact/dropdown";
import { HistoryTabProps } from "./types";
import { utils } from "../../utils/utils";

const HistoryTab: React.FC<HistoryTabProps> = ({
  historyItems,
  isLoadingHistory,
  onFileClick,
  onDownloadLogs,
  onDownloadErrorLog,
  onViewErrorDetails,
  merchants = [],
  selectedHistoryMerchant,
  onHistoryMerchantChange,
  isSuperAdmin = false
}) => {
  const [sortBy, setSortBy] = useState<'uploadTime' | 'status' | 'totalAmount'>('uploadTime');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const handleSort = (field: 'uploadTime' | 'status' | 'totalAmount') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const handleMerchantChange = (e: any) => {
    if (onHistoryMerchantChange) {
      onHistoryMerchantChange(e.value);
    }
  };

  const sortedHistory = [...historyItems].sort((a, b) => {
    let aValue: any, bValue: any;

    switch (sortBy) {
      case 'uploadTime':
        aValue = new Date(a.uploadTime).getTime();
        bValue = new Date(b.uploadTime).getTime();
        break;
      case 'status':
        aValue = a.status;
        bValue = b.status;
        break;
      case 'totalAmount':
        aValue = a.totalAmount;
        bValue = b.totalAmount;
        break;
      default:
        return 0;
    }

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const getStatusBadge = (status: string) => {
    const color = utils.getStatusBadgeColor(status);
    return <AppBadge variant={color as any}>{status}</AppBadge>;
  };

  return (
    <AppCard>
      <AppCard.Header>
        <i className="fas fa-history mr-2"></i>
        Upload History
      </AppCard.Header>
      <AppCard.Body className="p-6">
        {isSuperAdmin && (
          <div className="bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg p-4 mb-4">
            <label className="font-semibold text-sm text-neutral-600 dark:text-neutral-400 mb-3 block">Filter by Merchant:</label>
            <Dropdown
              value={selectedHistoryMerchant}
              onChange={handleMerchantChange}
              options={merchants}
              optionLabel="label"
              optionValue="value"
              filter
              showClear
              filterBy="label"
              filterPlaceholder="Search merchants..."
              placeholder="Select Merchant"
              className="w-full max-w-[300px] rounded-md border-2 border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-sm transition-all hover:border-primary-500"
            />
          </div>
        )}
        {isLoadingHistory ? (
          <div className="flex flex-col items-center justify-center p-12 min-h-[200px] animate-fade-in">
            <div className="mb-6 flex justify-center items-center w-[60px] h-[60px]">
              <div className="w-10 h-10 border-[3px] border-neutral-200 dark:border-neutral-800 border-t-primary-500 rounded-full animate-spin"></div>
            </div>
            <p className="text-neutral-500 dark:text-neutral-400 text-base font-medium text-center leading-relaxed animate-pulse">Loading history...</p>
          </div>
        ) : historyItems.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-inbox text-neutral-400" style={{ fontSize: '3rem' }}></i>
            <p className="text-neutral-500 mt-3">No files uploaded yet</p>
          </div>
        ) : (
          <div className="max-h-[600px] overflow-y-auto border border-neutral-200 dark:border-neutral-800 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-neutral-100 dark:bg-neutral-800 sticky top-0 z-10">
                <tr>
                  <th
                    className="py-3 px-4 text-left cursor-pointer select-none transition-colors hover:bg-neutral-200 dark:hover:bg-neutral-700"
                    onClick={() => handleSort('uploadTime')}
                  >
                    Upload Time
                    {sortBy === 'uploadTime' && (
                      <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ml-1`}></i>
                    )}
                  </th>
                  <th className="py-3 px-4 text-left">File Hash</th>
                  <th className="py-3 px-4 text-left">Payer IBAN</th>
                  <th
                    className="py-3 px-4 text-left cursor-pointer select-none transition-colors hover:bg-neutral-200 dark:hover:bg-neutral-700"
                    onClick={() => handleSort('totalAmount')}
                  >
                    Total Amount
                    {sortBy === 'totalAmount' && (
                      <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ml-1`}></i>
                    )}
                  </th>
                  <th className="py-3 px-4 text-left">Transactions</th>
                  <th
                    className="py-3 px-4 text-left cursor-pointer select-none transition-colors hover:bg-neutral-200 dark:hover:bg-neutral-700"
                    onClick={() => handleSort('status')}
                  >
                    Status
                    {sortBy === 'status' && (
                      <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ml-1`}></i>
                    )}
                  </th>
                  <th className="py-3 px-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedHistory.map((file) => (
                  <tr
                    key={file.id}
                    className="cursor-pointer transition-colors hover:bg-neutral-50 dark:hover:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700"
                    onClick={() => onFileClick(file)}
                  >
                    <td className="py-2 px-4">{utils.formatDateShort(new Date(file.uploadTime))}</td>
                    <td className="py-2 px-4">
                      <code className="bg-neutral-50 dark:bg-neutral-900 py-0.5 px-1.5 rounded text-[0.85rem] font-mono">{utils.formatHash(file.hash)}</code>
                    </td>
                    <td className="py-2 px-4">{file.payerIban}</td>
                    <td className="py-2 px-4 font-semibold text-green-600">{utils.formatAmountBGN(file.totalAmount)}</td>
                    <td className="py-2 px-4 text-center">
                      <AppBadge variant="info">{file.totalTransactionCount}</AppBadge>
                    </td>
                    <td className="py-2 px-4">{getStatusBadge(file.status)}</td>
                    <td className="py-2 px-4">
                      <div className="flex gap-2">
                        <Button
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onFileClick(file);
                          }}
                          className="min-w-[80px] font-medium rounded-md transition-all hover:-translate-y-px hover:shadow-md active:translate-y-0"
                          title="View Details"
                          icon="fas fa-eye"
                          label="View"
                        />

                        {/* {!isFailedStatus(file.status) && (
                          <Button
                            size="small"
                            severity="success"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDownloadLogs(file.id);
                            }}
                            className="min-w-[80px] font-medium rounded-md transition-all hover:-translate-y-px hover:shadow-md active:translate-y-0"
                            title="Download Logs"
                            icon="fas fa-download"
                            label="Logs"
                          />
                        )} */}

                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AppCard.Body>
    </AppCard>
  );
};

export default HistoryTab;
